import { IExtendedFormDataResponse, ISimpleResponse, IRequestParams, IResponse } from '@dash/apiInstance';
import { all, call, put, takeEvery } from 'redux-saga/effects';
import { sendRequest } from 'src/helpers/saga';
import ApiInstance from 'src/helpers/apiInstance';
import { executeAction } from '../../../../../../../../../helpers/api';
import { logger } from '../../../../../../../../../helpers/logger';
import { t } from '../../../../../../../../../i18n';
import notifications from '../../../../../../../../../redux/notifications/actions';
import actionTypes from './actionTypes';
import {
  ConfirmBankAddedAction,
  LoadFormInfoRequestAction,
  ProcessActionRequestAction,
  setupIntentBankAccountRequestAction,
} from './actions.d';
import { IFormInfo, IProcessActionResponse, ISetupIntentBankAccountResponse } from './types.d';
import bankAccountsActionTypes from '../../../../tabs/bankAccounts/actionTypes';
import actions from './actions';

const unitLoadFormDataApi = new ApiInstance('units/:id/bank_accounts/form_data');
const subunitsLoadFormDataApi = new ApiInstance('subunits/:id/bank_accounts/form_data');

const unitSetupApiBankAccounts = new ApiInstance('units/:id/setup_intents/becs_bank_accounts');
const subunitSetupApiBankAccounts = new ApiInstance('subunits/:id/setup_intents/becs_bank_accounts');

function* processAction({ payload }: ProcessActionRequestAction<{}, {}>) {
  try {
    const { url, method, data, type, id, key } = payload;

    const result: IResponse<IProcessActionResponse> = yield call<any, IRequestParams<{}, {}>, string>(
      sendRequest,
      executeAction,
      { url, method, data },
      notifications.REQUEST_ERROR,
    );

    const isCreateBankAccount = key === 'create_bank_account';

    if (result?.messages || result?.success) {
      if (isCreateBankAccount) {
        yield put({ type: bankAccountsActionTypes.LOAD_BANK_ACCOUNTS_REQUEST, payload: { type, id, pageSize: 9999 } });
        yield put({ type: actionTypes.ADD_BANK_ACCOUNT_SUCCESS });
        yield put({ type: actionTypes.CHANGE_MODAL_VISIBILITY });

        yield put(
          notifications.addNotification({
            headline: t('dash.common.notification.success_headline'),
            type: 'success',
            message: t('dash.common.notification.created_successfully', {
              item: t('dash.clients.subUnit.card_label'),
            }),
          }),
        );
      } else {
        yield put(
          notifications.addNotification({
            headline: t('dash.common.notification.success_headline'),
            type: 'success',
            message: result?.messages?.join(' ') || '',
          }),
        );
      }
    } else {
      yield put(
        notifications.addNotification({
          headline: t('dash.common.notification.error_headline'),
          type: 'error',
          message: t('dash.common.notification.something_went_wrong'),
        }),
      );

      if (isCreateBankAccount) {
        yield put({ type: actionTypes.ADD_BANK_ACCOUNT_ERROR, payload: { data: result } });
      }
    }
  } catch (error) {
    logger.error(error);
  }
}

function* loadFormInfo({ payload }: LoadFormInfoRequestAction<{}, {}>) {
  try {
    const { id, type } = payload;

    const api = type === 'subUnit' ? subunitsLoadFormDataApi : unitLoadFormDataApi;

    const result: IExtendedFormDataResponse<IFormInfo> = yield call<any, IRequestParams<{}, {}>, string>(
      sendRequest,
      api.get,
      { pathParams: { id } },
      notifications.REQUEST_ERROR,
    );

    if (result.status === 200 && result.data) {
      yield put(actions.loadFormInfoSuccess({ data: result.data }));
    } else {
      yield put(actions.loadFormInfoError());
    }
  } catch (error) {
    logger.error(error);
  }
}

function* confirmBankAccountAdded({ payload }: ConfirmBankAddedAction) {
  const { becsId, clientId, type } = payload;

  try {
    const apiPrefix = type === 'subUnit' ? 'subunits' : 'units';
    const confirmAddApi = new ApiInstance(
      `${apiPrefix}/${clientId}/payment_methods/becs_bank_accounts/${becsId}/added`,
    );

    const result: ISimpleResponse<{}> = yield call(sendRequest, confirmAddApi.post, {}, notifications.REQUEST_ERROR);

    if (result.status === 200) {
      yield put({
        type: bankAccountsActionTypes.LOAD_BANK_ACCOUNTS_REQUEST,
        payload: { type, id: clientId, pageSize: 9999 },
      });
      yield put({ type: actionTypes.ADD_BANK_ACCOUNT_SUCCESS });
      yield put({ type: actionTypes.CHANGE_MODAL_VISIBILITY });

      yield put(
        notifications.addNotification({
          headline: t('dash.common.notification.success_headline'),
          type: 'success',
          message: t('dash.common.notification.added_successfully', {
            item: t('dash.units.unit.bankAccounts.bank_account_label'),
          }),
        }),
      );
    } else {
      yield put(
        notifications.addNotification({
          headline: t('dash.common.notification.error_headline'),
          type: 'error',
          message: t('dash.common.notification.something_went_wrong'),
        }),
      );
    }
  } catch (error) {
    logger.error(error);
  }
}

function* setupIntentBankAccount({ payload }: setupIntentBankAccountRequestAction) {
  const { id, type } = payload;

  try {
    const api = type === 'subUnit' ? subunitSetupApiBankAccounts : unitSetupApiBankAccounts;

    const result: ISimpleResponse<ISetupIntentBankAccountResponse> = yield call<any, IRequestParams<{}, {}>, string>(
      sendRequest,
      api.post,
      { pathParams: { id } },
      notifications.REQUEST_ERROR,
    );

    if (result.status === 200) {
      const { data } = result;

      if (data.client_secret) {
        yield put(actions.setupIntentBankAccountSuccess({ clientSecret: data.client_secret }));
      }
    } else {
      yield put(
        notifications.addNotification({
          headline: t('dash.common.notification.error_headline'),
          type: 'error',
          message: t('dash.common.notification.something_went_wrong'),
        }),
      );
    }
  } catch (error) {
    logger.error(error);
  }
}

export default function* rootSaga() {
  yield all([takeEvery(actionTypes.PROCESS_ACTION_REQUEST, processAction)]);
  yield all([takeEvery(actionTypes.LOAD_FORM_INFO_REQUEST, loadFormInfo)]);
  yield all([takeEvery(actionTypes.SETUP_INTENT_BANK_ACCOUNT_REQUEST, setupIntentBankAccount)]);
  yield all([takeEvery(actionTypes.CONFIRM_BANK_ADDED_REQUEST, confirmBankAccountAdded)]);
}
